import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Select } from "antd";
import styles from "./BenchmarkModal.module.scss";
import { getKeywordStatistics } from "Explore/Services/KeywordBenchmarking";

const PREFERRED_DEFAULTS = [
  "artificial intelligence",
  "deep learning",
  "climate change"
];
const DEFAULT_INSTITUTION_COUNT = 3;

const BenchmarkModal = ({
  isOpen,
  onClose,
  onCompare,
  allKeywords = [],
  allInstitutions = [],
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  React.useEffect(() => {
    if (!allKeywords.length || !allInstitutions.length || initialized) return;

    const lower = allKeywords.map((kw) => kw.toLowerCase());
    const matched = PREFERRED_DEFAULTS.filter((pk) => lower.includes(pk));
    setSelectedKeywords(
      matched.length >= 5 ? matched : allKeywords.slice(0, 5)
    );

    setSelectedInstitutionIds(
      allInstitutions.slice(0, DEFAULT_INSTITUTION_COUNT).map((i) => i.id)
    );
    setInitialized(true);
  }, [allKeywords, allInstitutions, initialized]);

  const handleCompare = useCallback(async () => {
    if (!selectedKeywords.length || !selectedInstitutionIds.length) return;
    setIsLoading(true);
    try {
      const res = await getKeywordStatistics({
        keywords: selectedKeywords,
        institution_ids: selectedInstitutionIds,
      });
      onCompare({
        keywords: selectedKeywords,
        institutionIds: selectedInstitutionIds,
        data: res || [],
      });
      onClose();
    } catch {
      // keep modal open on error
    } finally {
      setIsLoading(false);
    }
  }, [selectedKeywords, selectedInstitutionIds, onCompare, onClose]);

  const keywordOptions = allKeywords.map((kw) => ({ label: kw, value: kw }));
  const institutionOptions = allInstitutions.map((inst) => ({
    label: inst.name,
    value: inst.id,
  }));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          background: "#fff",
          color: "#0f3460",
          padding: "20px 24px",
          minHeight: "60px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <CompareArrowsIcon sx={{ fontSize: 24 }} />
          <span style={{ fontWeight: 700, fontSize: "1.1rem" }}>
            Benchmark by Research Topics
          </span>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 16,
            color: "#0f3460",
            backgroundColor: "rgba(0,0,0,0.05)",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ padding: "24px", marginTop: "24px", background: "#fff" }}
      >
        <div className={styles["modal-body"]}>
          <div className={styles["filter-group"]}>
            <label className={styles["filter-label"]}>Research Topic</label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="Search and select keywords"
              options={keywordOptions}
              value={selectedKeywords}
              onChange={(val) => setSelectedKeywords(val || [])}
              size="large"
              popupClassName={styles["select-dropdown"]}
            />
          </div>

          <div className={styles["filter-group"]}>
            <label className={styles["filter-label"]}>Institution</label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder="Search and select institutions"
              options={institutionOptions}
              value={selectedInstitutionIds}
              onChange={(val) => setSelectedInstitutionIds(val || [])}
              size="large"
              popupClassName={styles["select-dropdown"]}
            />
          </div>

          <button
            className={styles["compare-btn"]}
            onClick={handleCompare}
            disabled={
              !selectedKeywords.length ||
              !selectedInstitutionIds.length ||
              isLoading
            }
          >
            <CompareArrowsIcon sx={{ fontSize: 18 }} />
            {isLoading ? "Comparing..." : "Compare"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BenchmarkModal;
